/**
 * One-line structured logs from server-side rendering, for Logtrail.
 *
 * WHY THIS EXISTS. We had no visibility into SSR at all: how long the CMS API takes per
 * page, which pages are slow, or whether a response came from Next's Data Cache or the
 * origin. Every judgement about server performance was inferred from PageSpeed, which is a
 * lab simulation on shared hardware, or from curl on a laptop — neither of which measures
 * what our server actually does per request.
 *
 * WHY NOT NEXT'S BUILT-IN. `logging.fetches` in next.config only works in development —
 * it is implemented under next/dist/server/dev/ (log-requests.js) and is absent from a
 * production server. So production fetch visibility has to be our own.
 *
 * WHICH CONTAINER. These lines come out of the WEBAPP container, because that is where
 * SSR runs. The Express container only sees the calls that actually reach it — i.e. cache
 * misses — so it can never tell you the hit rate.
 *
 * One JSON object per line, no multi-line output, so Logtrail can parse each entry whole.
 * Never throws: a logging failure must not take down a page render.
 */

/**
 * Below this many milliseconds a fetch cannot have crossed the network, so it came from
 * Next's Data Cache (a local disk read). Next does not expose hit/miss on the public API,
 * and the two populations are far apart in practice — cache reads land around 1-5ms while
 * the CMS API measures 40-150ms — so the inference is safe at this threshold. It is still
 * an INFERENCE: treat `source` as a strong hint, and `ms` as the fact.
 */
export const CACHE_HIT_MAX_MS = 15;

export type ServerLogFields = {
  /** Event name, snake_case, stable — this is what you filter on in Logtrail. */
  evt: string;
  /** Request path being rendered, when known. */
  path?: string | null;
  /** Tenant key resolved from the request Host, when known. */
  tenant?: string | null;
  /** Duration of the measured operation in whole milliseconds. */
  ms?: number;
  /** HTTP status of the upstream call, when there was one. */
  status?: number;
  /** Response size in bytes, when cheaply known. */
  bytes?: number;
  /** "cache" | "origin" — inferred from `ms`, see CACHE_HIT_MAX_MS. */
  source?: string;
  /** Anything else worth carrying; keep it flat and small. */
  [key: string]: unknown;
};

/**
 * Emit one structured line. Server-only and silent in the browser: these fields describe
 * work the server did, and shipping them to a client console would be noise at best.
 */
export function serverLog(fields: ServerLogFields): void {
  // Always on, deliberately. There is no env kill-switch: the volume is trivial (a few
  // thousand lines a day at current traffic) and the value is in having the history when
  // something regresses — a switch that gets flipped off "temporarily" is how you end up
  // with no data on the day you need it. The browser guard below is the only suppression.
  if (typeof window !== "undefined") return;
  try {
    /*
      console.warn, NOT console.log — and this is not a style choice.

      next.config.mjs sets `compiler.removeConsole` in production with
      `exclude: ["error", "warn"]`, so the compiler STRIPS every console.log from the
      production bundle. A logger built on console.log works perfectly in dev and emits
      absolutely nothing once deployed, which is the worst possible failure mode for
      observability: you only discover it when you go looking for data that was never
      written. (That is exactly what happened here — these lines were missing from
      LogTrail on dev until this was changed.)

      warn is also the honest stream for this: these are operational records meant to be
      collected, not debug chatter. Do not "tidy" this back to console.log.
    */
    console.warn(JSON.stringify({ ts: new Date().toISOString(), ...fields }));
  } catch {
    // A log line is never worth failing a render for.
  }
}

/** `source` for a fetch that took `ms`. See CACHE_HIT_MAX_MS for why this is an inference. */
export function inferSource(ms: number): "cache" | "origin" {
  return ms < CACHE_HIT_MAX_MS ? "cache" : "origin";
}
